import React from "react";

import FlowProvider from "../providers/FlowProvider";
import { CollaborationProvider } from "../providers/CollaborationProvider";
import StarterProvider from "../providers/StarterProvider";
import DialogProvider from "../providers/DialogProvider";
import { NodeCatalogDrawerProvider } from "../providers/packages";
import { AgentCatalogDrawerProvider } from "../providers/AgentCatalogDrawerProvider";
import { DatasetCatalogDrawerProvider } from "../providers/datasetCatalog";
import { ModelCatalogDrawerProvider } from "../providers/modelCatalog";
import { ScenarioCatalogDrawerProvider } from "../providers/scenarioCatalog";
import { DiscoveryCatalogDrawerProvider } from "../providers/discoveryCatalog";
import { ApiSettingsDrawerProvider } from "../providers/ApiSettingsDrawerProvider";
import { MonitorDrawerProvider } from "../providers/MonitorDrawerProvider";
import { ProvenanceDrawerProvider } from "../providers/ProvenanceDrawerProvider";
import { PackagePaletteProvider } from "../providers/packages";
import { DatasetPaletteProvider } from "../providers/DatasetPaletteContext";
import { ProjectLoader } from "./ProjectLoader";
import { CanvasDatasetDetailsProvider } from "./datasets/catalog/CanvasDatasetDetailsProvider";
import { ScenarioUiProvider } from "./scenarios/scenarioUi";

/**
 * Everything a dataflow's nodes need, for either route that renders them.
 *
 * The canvas and the dashboard mount the same node components, so they need the
 * same providers: the node header reads the dataset palette and the starter
 * store, an unresolved node offers the node catalog, and all of those throw
 * rather than degrade when their provider is missing. One stack, so a provider
 * added for the canvas cannot be missing from the dashboard.
 *
 * ``presentation`` says which route this is. It switches three things:
 * collaboration is left out (a dashboard is a view, not a session, and
 * ``useCollab`` is a no-op outside its provider by design), ``FlowProvider``
 * publishes ``dashboardOn``, and the loader lays the graph out as tiles instead
 * of a canvas.
 */
export const DataflowProviders: React.FC<{
  children: React.ReactNode;
  presentation?: boolean;
}> = ({ children, presentation = false }) => {
  const body = (
    // CollaborationProvider must wrap FlowProvider: FlowProvider binds the
    // canvas to the dataflow's doc (``useDocBinding``) and reads the tab's role
    // and the room's events (``useCollab()``), and a context only reaches
    // *descendants*. Putting it on the inside would hand FlowProvider no doc
    // and the no-op default value.
    <FlowProvider dashboardOn={presentation}>
      {/* Above the drawers and the palettes: every one of them opens a
          dataset's details through it, and the modal reads the live graph. */}
      <CanvasDatasetDetailsProvider>
      {/* The top bar's API Settings and Monitor open these drawers here, in
          place of their pages, so the dataflow stays open. */}
      <ApiSettingsDrawerProvider>
      <MonitorDrawerProvider>
      {/* View > Provenance opens the dataflow's history here. Inside
          FlowProvider: putting a version back loads it onto the canvas. */}
      <ProvenanceDrawerProvider>
      {/* NodeCatalogDrawerProvider must sit INSIDE FlowProvider: the drawer
          calls useFlowContext to auto-save unsaved dataflows on Install, and
          a portal preserves React tree context, not DOM position. Outside
          FlowProvider, useFlowContext returns no-op defaults and Install
          silently does nothing. */}
      <NodeCatalogDrawerProvider>
        <DatasetCatalogDrawerProvider>
          <AgentCatalogDrawerProvider>
            {/* Inside FlowProvider like its peers: a model's details ask
                before a link leaves a dataflow with unsaved changes. */}
            <ModelCatalogDrawerProvider>
              {/* The scenario pointed at in the Scenario drawer's In project
                  tab, which the canvas marks: above both. */}
              <ScenarioUiProvider>
              {/* Beside the Model drawer's, for the same reason: opening a
                  scenario's project asks before leaving unsaved changes. */}
              <ScenarioCatalogDrawerProvider>
              {/* Inside the Model drawer's provider: a model added here is
                  viewed by opening that drawer. */}
              <DiscoveryCatalogDrawerProvider>
                <StarterProvider>
                  <ProjectLoader presentation={presentation}>
                    <PackagePaletteProvider>
                      <DatasetPaletteProvider>{children}</DatasetPaletteProvider>
                    </PackagePaletteProvider>
                  </ProjectLoader>
                </StarterProvider>
              </DiscoveryCatalogDrawerProvider>
              </ScenarioCatalogDrawerProvider>
              </ScenarioUiProvider>
            </ModelCatalogDrawerProvider>
          </AgentCatalogDrawerProvider>
        </DatasetCatalogDrawerProvider>
      </NodeCatalogDrawerProvider>
      </ProvenanceDrawerProvider>
      </MonitorDrawerProvider>
      </ApiSettingsDrawerProvider>
      </CanvasDatasetDetailsProvider>
    </FlowProvider>
  );

  return (
    <DialogProvider>
      {presentation ? body : <CollaborationProvider>{body}</CollaborationProvider>}
    </DialogProvider>
  );
};

export default DataflowProviders;
